/**
 * Canonical USD (cents/minor-unit) prices for the one-time global product
 * families whose rate-card page and Gumroad catalog previously hardcoded
 * separate numbers: Report (`/reports`) and Advertiser (`/advertise`).
 *
 * Keyed by each family's plain rate-card key ("single"/"bundle",
 * "sidebar"/"blog"/"category"/"bundle") — NOT the Mayar `ProductKey` type —
 * so this module has ZERO imports. That matters: `mayar/products.ts` imports
 * FROM `reports/pricing.ts` and `ads/pricing.ts` to build `REPORT_PRODUCTS` /
 * `ADVERTISER_PRODUCTS` (IDR), and `catalog-onetime-global.ts` imports FROM
 * `mayar/products.ts` for its `isXProductKey` guards. If `reports/pricing.ts`
 * or `ads/pricing.ts` imported the USD numbers back from
 * `catalog-onetime-global.ts`, that would close a require cycle
 * (ads/pricing → catalog-onetime-global → mayar/products → ads/pricing). This
 * leaf module breaks the cycle: everyone reads the SAME numbers from here,
 * nobody reads them from each other.
 *
 * Incident (2026-07-12): `REPORT_PLANS.usdOnce` ($29/$79, `reports/pricing.ts`)
 * and `ADVERTISE_PLANS.usdMonthly` ($79/$59/$49/$149, `ads/pricing.ts`) were
 * hand-typed independently of `REPORT_USD_MINOR` ($39/$99) and
 * `ADVERTISER_USD_MINOR` ($69/$49/$39/$129) in `catalog-onetime-global.ts` —
 * the rate-card page quoted one price, the Gumroad product actually charged
 * another. Fixed by making every USD display value in this repo derive from
 * these constants (`tests/unit/pricing/price-parity.test.ts` locks it).
 */

/** USD cents for each Role Market Report plan. Single source for `usdOnce`. */
export const REPORT_USD_MINOR_BY_PLAN: Record<"single" | "bundle", number> = {
	single: 3_900, // $39
	bundle: 9_900, // $99
};

/** USD cents for each advertiser placement. Single source for `usdMonthly`. */
export const ADVERTISER_USD_MINOR_BY_PLACEMENT: Record<
	"sidebar" | "blog" | "category" | "bundle",
	number
> = {
	sidebar: 6_900, // $69
	blog: 4_900, // $49
	category: 3_900, // $39
	bundle: 12_900, // $129
};

/**
 * USD cents for the flat self-serve advertiser plans (all placements rotate).
 * Single source for `ADVERTISE_FLAT_PLANS[*].usd` and the global checkout.
 */
export const ADVERTISER_FLAT_USD_MINOR: Record<"weekly" | "monthly", number> = {
	weekly: 900, // $9
	monthly: 2_500, // $25
};

/** USD cents for any advertiser plan key, legacy placement or flat. */
export function advertiserUsdMinorForPlan(
	planKey:
		| keyof typeof ADVERTISER_USD_MINOR_BY_PLACEMENT
		| keyof typeof ADVERTISER_FLAT_USD_MINOR,
): number {
	return planKey === "weekly" || planKey === "monthly"
		? ADVERTISER_FLAT_USD_MINOR[planKey]
		: ADVERTISER_USD_MINOR_BY_PLACEMENT[planKey];
}
